/**
 * v3.0.0 の評価詳細ビュー
 *
 * バージョン分離ルール: 他バージョンの詳細ビューや型を import せず、v3.0.0 用に自前で持つ。
 * v2.2.0 との差分:
 * - 軸は固定の5軸ではなく、保存された axes の並びと満点をそのまま出す（カテゴリで軸と配点が変わるため）
 * - お題カテゴリを出す
 * - 内容の項目は判定の根拠（evidence）を持つので、表に列を足す
 */

import React from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ExpandMore,
  CheckCircle,
  TipsAndUpdates,
  ThumbUp,
  AutoAwesome,
  Psychology,
  FormatQuote,
} from "@mui/icons-material";
import {
  EvaluationAxisResultV3_0,
  EvaluationDetailV3_0_0,
  EvaluationLlmModelsV3_0_0,
  ImprovementV3_0,
  ImprovementPriorityV3_0,
  RubricCategoryKind,
} from "../../../types";
import TopicBadges from "../../common/TopicBadges";

interface Props {
  evaluation: EvaluationDetailV3_0_0;
}

const CATEGORY_LABELS: Record<RubricCategoryKind, string> = {
  preference: "思いを伝える",
  claim: "主張を伝える",
  claimWithSourceText: "主張を伝える（課題文あり）",
};

// 優先度は改善効果の大きさ。並び順ではなく効果の大小として見せる
const PRIORITY_LABELS: Record<ImprovementPriorityV3_0, string> = {
  high: "効果大",
  medium: "効果中",
  low: "効果小",
};

const PRIORITY_COLORS: Record<ImprovementPriorityV3_0, "error" | "warning" | "default"> = {
  high: "error",
  medium: "warning",
  low: "default",
};

const LLM_MODEL_LABELS: Record<keyof EvaluationLlmModelsV3_0_0, string> = {
  readingScoring: "読解の採点",
  contentScoring: "内容の採点",
  structureExpressionScoring: "構成と表現力の採点",
  improvement: "改善提案の生成",
};

interface ScoreRowProps {
  label: string;
  score: number;
  maxScore: number;
  explanation: string;
}

const ScoreRow: React.FC<ScoreRowProps> = ({ label, score, maxScore, explanation }) => {
  const ratio = maxScore > 0 ? score / maxScore : 0;
  const color: "success" | "primary" | "warning" | "error" =
    ratio >= 0.8 ? "success" : ratio >= 0.6 ? "primary" : ratio >= 0.4 ? "warning" : "error";

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold" color={`${color}.main`}>
          {score} / {maxScore}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={ratio * 100}
        color={color}
        sx={{ height: 8, borderRadius: 1 }}
      />
      {explanation && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {explanation}
        </Typography>
      )}
    </Box>
  );
};

const AxisChecklistTable: React.FC<{ axisResult: EvaluationAxisResultV3_0 }> = ({ axisResult }) => {
  const checklist = axisResult.scoredChecklist;
  if (checklist.length === 0) return null;

  // 根拠は内容の項目だけが持つ。持つ項目がある軸だけ列を出す
  const hasEvidenceColumn = checklist.some((checklistItem) => checklistItem.evidence !== undefined);
  const deductedItemCount = checklist.filter(
    (checklistItem) => checklistItem.point < checklistItem.maxPoint
  ).length;
  const isFullScore = deductedItemCount === 0;

  return (
    <Box sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="subtitle2" fontWeight="bold">
          {axisResult.label}
        </Typography>
        <Chip
          label={isFullScore ? "満点" : `${deductedItemCount}項目で減点`}
          size="small"
          variant="outlined"
          color={isFullScore ? "success" : "warning"}
        />
        <Typography
          variant="body2"
          fontWeight="bold"
          color={isFullScore ? "success.main" : "warning.main"}
          sx={{ ml: "auto" }}
        >
          {axisResult.score} / {axisResult.maxScore} 点
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>チェック項目</TableCell>
            <TableCell>判定理由</TableCell>
            {hasEvidenceColumn && <TableCell>根拠の箇所</TableCell>}
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              加点
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checklist.map((checklistItem, index) => (
            <TableRow key={index}>
              <TableCell sx={{ verticalAlign: "top" }}>{checklistItem.item}</TableCell>
              <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>
                {checklistItem.reason}
              </TableCell>
              {hasEvidenceColumn && (
                <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>
                  {checklistItem.evidence || "-"}
                </TableCell>
              )}
              <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={
                    checklistItem.point >= checklistItem.maxPoint
                      ? "success.main"
                      : checklistItem.point > 0
                        ? "warning.main"
                        : "error.main"
                  }
                >
                  {checklistItem.point} / {checklistItem.maxPoint}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ImprovementItem: React.FC<{ improvement: ImprovementV3_0; index: number }> = ({
  improvement,
  index,
}) => {
  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="body2" fontWeight="bold" color="info.main">
          {index + 1}.
        </Typography>
        <Typography variant="body2" fontWeight="bold">
          {improvement.title}
        </Typography>
        <Chip
          label={improvement.scope === "partial" ? "部分" : "全体"}
          size="small"
          variant="outlined"
          color={improvement.scope === "partial" ? "info" : "secondary"}
        />
        <Chip
          label={PRIORITY_LABELS[improvement.priority]}
          size="small"
          variant="outlined"
          color={PRIORITY_COLORS[improvement.priority]}
        />
      </Box>
      {improvement.targetQuote && (
        <Box display="flex" alignItems="flex-start" gap={0.5} sx={{ ml: 3, mb: 0.5 }}>
          <FormatQuote fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {improvement.targetQuote}
          </Typography>
        </Box>
      )}
      <Typography variant="body2" sx={{ ml: 3, whiteSpace: "pre-wrap" }}>
        {improvement.explanation}
      </Typography>
      {improvement.rewriteExample && (
        <Paper variant="outlined" sx={{ ml: 3, mt: 1, p: 1, bgcolor: "grey.50" }}>
          <Typography variant="caption" color="text.secondary" display="block">
            書き換え例
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {improvement.rewriteExample}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

const EvaluationDetailV3_0_0View: React.FC<Props> = ({ evaluation }) => {
  const { axes, category, improvement } = evaluation.result;
  const claimTypeForBadge =
    evaluation.topic.claimType == null ? undefined : evaluation.topic.claimType;
  const llmModels = evaluation.llmModels;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
        <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Typography variant="subtitle1" fontWeight="bold">
            <Psychology sx={{ mr: 1, verticalAlign: "middle" }} />
            軸別スコア
          </Typography>
          {category && <Chip label={CATEGORY_LABELS[category]} size="small" variant="outlined" />}
        </Box>
        {axes.map((axisResult) => (
          <ScoreRow
            key={axisResult.axis}
            label={axisResult.label}
            score={axisResult.score}
            maxScore={axisResult.maxScore}
            explanation={axisResult.explanation}
          />
        ))}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <AutoAwesome sx={{ mr: 1, verticalAlign: "middle" }} />
          総評
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
          {improvement.shortReview}
        </Typography>
      </Paper>

      {improvement.strengths.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "success.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">
            <ThumbUp sx={{ mr: 1, verticalAlign: "middle" }} />
            良い点
          </Typography>
          <List dense disablePadding>
            {improvement.strengths.map((strength, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={strength} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {improvement.improvements.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "info.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
            <TipsAndUpdates sx={{ mr: 1, verticalAlign: "middle" }} />
            改善提案
          </Typography>
          {improvement.improvements.map((improvementItem, index) => (
            <ImprovementItem key={index} improvement={improvementItem} index={index} />
          ))}
        </Paper>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            採点の内訳
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {axes.map((axisResult) => (
            <AxisChecklistTable key={axisResult.axis} axisResult={axisResult} />
          ))}
        </AccordionDetails>
      </Accordion>

      {llmModels && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              採点に使ったモデル
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableBody>
                {(Object.keys(LLM_MODEL_LABELS) as Array<keyof EvaluationLlmModelsV3_0_0>).map(
                  (modelKey) => (
                    <TableRow key={modelKey}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {LLM_MODEL_LABELS[modelKey]}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {llmModels[modelKey] ||
                          (modelKey === "readingScoring" ? "(課題文型以外は採点なし)" : "(未記録)")}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              元の問題文
            </Typography>
            <TopicBadges
              difficulty={evaluation.topic.difficulty}
              genre={evaluation.topic.genre}
              questionType={evaluation.topic.questionType}
              claimType={claimTypeForBadge}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {evaluation.topic.theme && (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {evaluation.topic.theme}
            </Typography>
          )}
          {evaluation.topic.content && (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.topic.content}
            </Typography>
          )}
          {evaluation.topic.background && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                背景説明
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.topic.background}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {evaluation.input.understanding && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              ユーザーの理解
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              主張
            </Typography>
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.claim || "(未入力)"}
              </Typography>
            </Paper>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              根拠
            </Typography>
            <Paper sx={{ p: 1.5, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.grounds || "(未入力)"}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            ユーザーの回答
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.input.userOpinion}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {improvement.improvedExample && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              AI改善例
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, bgcolor: "primary.50" }}>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {improvement.improvedExample}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default EvaluationDetailV3_0_0View;
